/**
 * Home Saved Search Updates derivations (guide §7).
 *
 * Input rows come from the `loadSavedSearchUpdates` slot of the Home data
 * adapter: one row per Saved Search with the count (and optionally the ids)
 * of companies the user has not reviewed yet. The KPI and the widget read the
 * same filtered list, so the KPI counts Saved Searches — never companies.
 */

export const HOME_SAVED_SEARCH_WIDGET_LIMIT = 4;

/** Nav-state key read by the Saved Search page to highlight new rows. */
export const FOCUS_NEW_RESULTS_STATE = "focusNewResults";

function toTimestamp(value) {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
}

export function normalizeSavedSearchUpdate(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || raw.workspaceId || "").trim();
  if (!id) return null;
  const newCompanyIds = Array.isArray(raw.newCompanyIds)
    ? raw.newCompanyIds.map(String).filter(Boolean)
    : [];
  const unseenCount = Number(raw.unseenCount);
  return {
    id,
    name: String(raw.name || "").trim() || "Untitled Saved Search",
    unseenCount: Number.isFinite(unseenCount) && unseenCount > 0 ? unseenCount : newCompanyIds.length,
    newCompanyIds,
    companyCount: Number.isFinite(Number(raw.companyCount)) ? Number(raw.companyCount) : null,
    updatedAt: raw.updatedAt || null,
  };
}

/** Saved Searches with at least one unseen company, most recently updated first. */
export function deriveSavedSearchUpdates(rows) {
  const updates = (Array.isArray(rows) ? rows : [])
    .map(normalizeSavedSearchUpdate)
    .filter((row) => row && row.unseenCount > 0)
    .sort((a, b) => toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt));
  return { updates, updatedCount: updates.length };
}

export function formatNewMatchDelta(update) {
  return update?.unseenCount > 0 ? `+${update.unseenCount} new` : null;
}

/** Location state for opening a Saved Search focused on its new companies. */
export function buildFocusNewResultsState(update) {
  if (!update || !(update.unseenCount > 0)) return null;
  return {
    [FOCUS_NEW_RESULTS_STATE]: true,
    newCompanyIds: update.newCompanyIds.length > 0 ? update.newCompanyIds : undefined,
    newCompanyCount: update.unseenCount,
  };
}
